import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import Layout from './Layout';
import Footer from './ui/Footer';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import './ui/HMSHeaderStyles.css';
import './Settings.css';
import withAuth from '../utils/withAuth';
import axiosInstance from '../utils/axiosConfig';
import hmsLogoWhite from '../assets/images/IMT_logo.png';
import hmsAdminService from '../services/hmsAdminService';

function PasswordManagement() {
  const history = useHistory();
  const location = useLocation();
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    valid: false,
    length: false,
    hasNumber: false,
    hasSpecial: false,
    hasUpper: false,
    hasLower: false,
    score: 0,
    strength: 'weak'
  });


  // Check password strength whenever new password changes
  useEffect(() => {
    validatePassword(newPassword);
  }, [newPassword]);

  const validatePassword = (password) => {
    const strength = {
      valid: false,
      length: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password)
    };
    
    // Calculate score based on number of requirements met
    strength.score = [
      strength.length,
      strength.hasNumber,
      strength.hasSpecial,
      strength.hasUpper,
      strength.hasLower
    ].filter(Boolean).length;
    
    // Determine strength level based on score
    if (strength.score === 0 || !password) {
      strength.strength = 'weak';
    } else if (strength.score >= 3 && strength.score < 5) {
      strength.strength = 'medium';
    } else if (strength.score === 5) {
      strength.strength = 'strong';
    } else {
      strength.strength = 'weak'; // 1-2 requirements met
    }
    
    // Password is valid if it meets all criteria
    strength.valid = strength.length && strength.hasNumber && 
                     strength.hasSpecial && strength.hasUpper && strength.hasLower;
    
    setPasswordStrength(strength);
    return strength.valid;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (!passwordStrength.valid) {
      setError('Password does not meet security requirements');
      return;
    }

    try {
      const response = await axiosInstance.post('/api/authentication/change-password/', {
        current_password: currentPassword,
        new_password: newPassword
      });

      setMessage('Password successfully updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred while updating password');
    }
  };

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="password-security-layout">
        {/* HMS Header System - Logo + Screen Title */}
        <div className="password-security-header">
          <div className="header-logo">
            <img src={hmsLogoWhite} alt="IMT" className="header-logo-img" />
            <div>
              <h1 className="hms-screen-title">Password Security</h1>
              <p className="password-subtitle">Update your password and maintain account security</p>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="password-security-content">
          <div className="password-card">
            <div className="password-card-header">
              <FontAwesomeIcon icon={faLock} className="password-card-icon" />
              <h2>Change Password</h2>
            </div>

            {message && <div className="success-message">{message}</div>}
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-group form-group-full-width">
                <label>Current Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    placeholder="Enter your current password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                  >
                    <FontAwesomeIcon 
                      icon={showCurrentPassword ? faEyeSlash : faEye} 
                      className="eye-icon"
                    />
                  </button>
                </div>
                <div className="help-text">
                  Please enter your existing password to verify your identity
                </div>
              </div>
              
              {/* New Password and Confirm Password Side by Side */}
              <div className="password-fields-row">
                <div className="form-group">
                  <label>New Password</label>
                  <div className="password-input-container">
                    <div className="password-input-wrapper">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        placeholder="Enter your new password"
                        className="password-input"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        aria-label={showNewPassword ? "Hide password" : "Show password"}
                      >
                        <FontAwesomeIcon 
                          icon={showNewPassword ? faEyeSlash : faEye} 
                          className="eye-icon"
                        />
                      </button>
                    </div>
                    <div className="password-strength-indicator">
                      <div className={`strength-bar ${passwordStrength.strength}`}>
                        <div className="strength-fill"></div>
                      </div>
                      <span className="strength-text">
                        {passwordStrength.strength.charAt(0).toUpperCase() + passwordStrength.strength.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="Re-enter your new password"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      <FontAwesomeIcon 
                        icon={showConfirmPassword ? faEyeSlash : faEye} 
                        className="eye-icon"
                      />
                    </button>
                  </div>
                  <div className="help-text">
                    {confirmPassword && newPassword !== confirmPassword ? (
                      <span style={{color: '#dc2626'}}>Passwords do not match</span>
                    ) : confirmPassword && newPassword === confirmPassword ? (
                      <span style={{color: '#28a745'}}>Passwords match</span>
                    ) : (
                      'Please confirm your new password'
                    )}
                  </div>
                </div>
              </div>

              {/* Password Requirements - Spans Full Width */}
              <div className="password-requirements-full-width">
                <div className="password-requirements-modern">
                  <h4>Password Requirements</h4>
                  <div className="requirements-grid">
                    <div className={`requirement-item ${passwordStrength.length ? "requirement-met" : "requirement-pending"}`}>
                      <div className="requirement-icon">
                        {passwordStrength.length ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                          </svg>
                        )}
                      </div>
                      <span>At least 8 characters</span>
                    </div>
                    
                    <div className={`requirement-item ${passwordStrength.hasUpper ? "requirement-met" : "requirement-pending"}`}>
                      <div className="requirement-icon">
                        {passwordStrength.hasUpper ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                          </svg>
                        )}
                      </div>
                      <span>One uppercase letter (A-Z)</span>
                    </div>
                    
                    <div className={`requirement-item ${passwordStrength.hasLower ? "requirement-met" : "requirement-pending"}`}>
                      <div className="requirement-icon">
                        {passwordStrength.hasLower ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                          </svg>
                        )}
                      </div>
                      <span>One lowercase letter (a-z)</span>
                    </div>
                    
                    <div className={`requirement-item ${passwordStrength.hasNumber ? "requirement-met" : "requirement-pending"}`}>
                      <div className="requirement-icon">
                        {passwordStrength.hasNumber ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                          </svg>
                        )}
                      </div>
                      <span>One number (0-9)</span>
                    </div>
                    
                    <div className={`requirement-item ${passwordStrength.hasSpecial ? "requirement-met" : "requirement-pending"}`}>
                      <div className="requirement-icon">
                        {passwordStrength.hasSpecial ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                          </svg>
                        )}
                      </div>
                      <span>One special character (!@#$%^&*)</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="password-submit-button"
                  disabled={!passwordStrength.valid || newPassword !== confirmPassword}
                >
                  <FontAwesomeIcon icon={faLock} style={{marginRight: '8px'}} />
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Hamster Chat Integration */}
        <HamsterFloatingButton />

        {/* Back to Top Button */}
        <BackToTopButton />

        {/* HMS Footer Component */}
        <Footer />
      </div>
    </Layout>
  );
}

export default withAuth(PasswordManagement); 